import { useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { z } from 'zod'

import { cn } from '@shared/lib/cn'

import styles from './styles.module.scss'

import CameraIcon from '/icons/camera-plus.svg'

interface UploadFileProps {
  accept: string[];
  errorText?: string;
  onFileAccepted?: (files: File[]) => void;
  maxFileSizeMB?: number;
  maxFilesCount?: number;
}

export default function UploadFile ({
  accept,
  errorText,
  onFileAccepted,
  maxFileSizeMB = 5,
  maxFilesCount = 5,
}: UploadFileProps) {
  const { t: tc } = useTranslation('common')
  const inputRef = useRef<HTMLInputElement>(null)
  const [ files, setFiles ] = useState<File[]>([])
  const [ error, setError ] = useState<string | null>(null)
  const [ isDragging, setIsDragging ] = useState(false)

  const mimeMap: Record<string, string> = {
    '.pdf': 'application/pdf',
    '.docx':
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.doc': 'application/msword',
    '.png': 'image/png',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.txt': 'text/plain',
  }

  const mimeTypes = accept
    .map((ext) => mimeMap[ext.toLowerCase()])
    .filter(Boolean)

  const validateFile = (file: File) => {
    const schema = z.object({
      name: z.string(),
      type: z.string().refine((type) => mimeTypes.includes(type), {
        message: errorText || 'Недопустимый формат файла.',
      }),
      size: z.number().refine((size) => size <= maxFileSizeMB * 1024 * 1024, {
        message: `Файл превышает ${maxFileSizeMB} МБ.`,
      }),
    })
    return schema.safeParse({
      name: file.name,
      type: file.type,
      size: file.size,
    })
  }

  const handleFiles = (incomingFiles: FileList | File[]) => {
    const accepted: File[] = []
    const inputFiles = Array.from(incomingFiles)

    if (files.length + inputFiles.length > maxFilesCount) {
      setError(`Максимум ${maxFilesCount} файлов.`)
      return
    }

    for (const file of inputFiles) {
      const parsed = validateFile(file)
      if (parsed.success) {
        accepted.push(file)
      }
      else if (!error) {
        setError(parsed.error.errors[0].message)
      }
    }

    if (accepted.length) {
      const updated = [ ...files, ...accepted ]
      setFiles(updated)
      setError(null)
      onFileAccepted?.(updated)
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    if (e.dataTransfer.files?.length) {
      handleFiles(e.dataTransfer.files)
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      handleFiles(e.target.files)
      e.target.value = ''
    }
  }

  const handleRemove = (index: number) => {
    const updated = files.filter((_, i) => i !== index)
    setFiles(updated)
    onFileAccepted?.(updated)
  }

  return (
    <div className={styles.root}>
      <div
        className={cn(styles.root__uploader, {
          [styles.over]: isDragging,
        })}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => {
          inputRef.current?.click()
        }}
      >
        <input
          ref={inputRef}
          className={styles.root__input}
          type="file"
          accept={accept.join(',')}
          multiple
          onChange={onInputChange}
        />

        <img className={styles.root__img} src={CameraIcon} alt="" />
        <Trans
          i18nKey="common:file_upload.full"
          values={{ count: maxFilesCount }}
          components={{
            1: <span className={styles.highlight} />,
            br: <br />,
          }}
        />

        {error && (
          <p className={styles.root__error}>
            {tc('error')}: {error}
          </p>
        )}
      </div>

      {files.length > 0 && (
        <ul className={styles.root__list}>
          {files.map((file, index) => (
            <li key={index} className={styles.root__item}>
              <span className={styles.root__filename}>{file.name}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleRemove(index)
                }}
              >
                x
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
